/**
 * RecentlyViewedRail - right-rail card listing tickets the current user
 * has opened recently. Customer-facing surface only (the agent rail
 * already covers this need via the Related-tickets recommender).
 *
 * Storage is localStorage-only - we deliberately do not persist this
 * server-side because (a) it's a per-browser convenience and (b) it
 * would leak read-receipts the customer didn't opt into. The key is
 * keyed by the logged-in user id so two users sharing a laptop don't
 * cross-pollute each other's recents. When the user id is unknown
 * (e.g. before /me resolves) the rail no-ops rather than dumping into
 * an anonymous bucket.
 */
import { Link } from "react-router-dom";

import { Card } from "@/components/ui";
import { useAuthStore } from "@/stores/auth";
import type { TicketStatus } from "@/types/api";

import { StatusBadge } from "./StatusBadge";

const STORAGE_PREFIX = "buyist-support:recently-viewed:";
const MAX_ENTRIES = 8;

export interface RecentlyViewedEntry {
  ticket_number: number;
  /** Customer-facing short identifier (e.g. "KU1" or "F-104944").
   * Optional for back-compat with entries written before 2026-05-17
   * when only ticket_number was stored; the Link href falls back to
   * ticket_number when missing. */
  public_code?: string;
  subject: string;
  status: TicketStatus;
  viewed_at: string;
}

function storageKey(userId: string): string {
  return `${STORAGE_PREFIX}${userId}`;
}

function readEntries(userId: string | undefined): RecentlyViewedEntry[] {
  if (!userId) return [];
  try {
    const raw = localStorage.getItem(storageKey(userId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentlyViewedEntry =>
        typeof e === "object" &&
        e !== null &&
        typeof (e as RecentlyViewedEntry).ticket_number === "number" &&
        typeof (e as RecentlyViewedEntry).subject === "string",
    );
  } catch {
    return [];
  }
}

function writeEntries(
  userId: string,
  entries: RecentlyViewedEntry[],
): void {
  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify(entries.slice(0, MAX_ENTRIES)),
    );
  } catch {
    /* noop */
  }
}

/**
 * Record a ticket as recently viewed for the given user. Safe to call
 * unconditionally - silently no-ops when user_id is missing.
 *
 * Newer entries are prepended; duplicates dedupe by ticket_number so
 * reopening a ticket bumps it to the top instead of doubling it up.
 */
export function recordRecentlyViewed(
  userId: string | undefined,
  entry: Omit<RecentlyViewedEntry, "viewed_at">,
): void {
  if (!userId) return;
  const existing = readEntries(userId);
  const filtered = existing.filter(
    (e) => e.ticket_number !== entry.ticket_number,
  );
  const next: RecentlyViewedEntry[] = [
    { ...entry, viewed_at: new Date().toISOString() },
    ...filtered,
  ].slice(0, MAX_ENTRIES);
  writeEntries(userId, next);
}

export interface RecentlyViewedRailProps {
  /** Exclude this ticket from the rendered list (the one the user is
   *  currently looking at). */
  excludeTicketNumber?: number;
}

export function RecentlyViewedRail({
  excludeTicketNumber,
}: RecentlyViewedRailProps): JSX.Element | null {
  const userId = useAuthStore((s) => s.user?.id);
  const entries = readEntries(userId).filter(
    (e) => e.ticket_number !== excludeTicketNumber,
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="hidden lg:block" data-testid="recently-viewed-rail">
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <i
            className="fa-duotone fa-thin fa-clock-rotate-left text-base text-neutral-500"
            aria-hidden="true"
          />
          <h2 className="text-sm font-semibold text-neutral-700">
            Recently viewed
          </h2>
        </div>
        <ul className="space-y-3">
          {entries.map((row) => (
            <li key={row.ticket_number}>
              <Link
                to={`/tickets/${(row.public_code ?? String(row.ticket_number)).toLowerCase()}`}
                className="block rounded-md border border-neutral-200 bg-white p-2 transition-colors hover:border-primary-300 hover:bg-primary-50"
                data-testid="recently-viewed-row"
              >
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-xs font-medium text-neutral-500">
                    #{row.public_code ?? row.ticket_number}
                  </span>
                  <StatusBadge status={row.status} viewer="customer" />
                </div>
                <div className="line-clamp-2 text-sm text-neutral-800">
                  {row.subject}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
}

export default RecentlyViewedRail;
